export const ROLES = ["admin", "sales", "logistics", "accounting"] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  "customers:read",
  "customers:write",
  "suppliers:read",
  "suppliers:write",
  "products:read",
  "products:write",
  "stock:read",
  "stock:write",
  "sales:read",
  "sales:write",
  // Invoices, credit notes and the accountant's ZIP export: not part of the warehouse's job.
  "invoices:read",
  "invoices:issue",
  "purchasing:read",
  "purchasing:write",
  // Supplier payments: money leaving the company, apart from placing and receiving orders.
  "purchasing:pay",
  "sav:read",
  "sav:write",
  "reports:read",
  // Weighted average costs, margins and the stock valuation (purchase prices stay with purchasing).
  "costs:read",
  "imports:run",
  "settings:manage",
  "users:manage",
  "audit:read",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const READ_ALL: Permission[] = PERMISSIONS.filter((p) => p.endsWith(":read"));

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS,
  sales: [
    ...READ_ALL.filter((p) => p !== "audit:read"),
    "customers:write",
    "products:write",
    "sales:write",
    // Invoices, credit notes and the accountant's ZIP export: not part of the warehouse's job.
    "invoices:read",
    "invoices:issue",
    "sav:write",
    "imports:run",
  ],
  // The guide: stock, shipments, receipts, purchasing, suppliers, products, after-sales; customers and
  // orders read-only. No invoices, no costs or margins, no supplier payments.
  logistics: [
    "customers:read",
    "suppliers:read",
    // Logistics gets the stock alerts: it sets thresholds, target stock and supplier links.
    "suppliers:write",
    "products:read",
    "products:write",
    "stock:read",
    "stock:write",
    "sales:read",
    "purchasing:read",
    "purchasing:write",
    "sav:read",
    "sav:write",
  ],
  accounting: READ_ALL,
};

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

export function can(role: Role | null | undefined, permission: Permission): boolean {
  return role ? ROLE_PERMISSIONS[role].includes(permission) : false;
}
